import type { Problem } from "./types";

/**
 * Plain-language text for the stable problem `code`s the FER API returns (RFC 9457). Every code the contract names
 * and every code the backend (apps/fire/api/lcc_fire) raises is here (tests/contract-sync.test.ts scans both), plus
 * the shared middleware codes and the client's own (network, unexpected reply, list too long).
 */
export const PROBLEM_MESSAGES: Readonly<Record<string, string>> = {
  // Shared middleware and sign-in
  unauthenticated: "Your session has expired. Sign in again to continue.",
  forbidden: "You are not allowed to do this here.",
  forbidden_role: "Your role does not allow this action.",
  forbidden_ward: "This record is outside the wards you are assigned to.",
  no_party: "Your account is not linked to a person record. Ask your administrator.",
  not_found: "We could not find that record. It may have been removed, or it is outside what you may see.",
  validation_failed: "Some details are missing or not valid. Check the form and try again.",
  bad_request: "The request could not be read. Refresh and try again.",
  bad_cursor: "The list could not be continued. Refresh and try again.",
  idempotency_key_required: "The request was missing its safety key. Please try again.",
  idempotency_key_invalid: "The request's safety key was not valid. Please try again.",
  idempotency_key_reused: "This request was already sent with different details. Start again.",
  idempotency_in_flight: "This request is still being processed. Wait a moment and try again.",
  rate_limited: "Too many requests at once. Wait a moment and try again.",
  token_not_accepted: "This sign-in is not accepted here. Sign in with your council staff account.",
  local_token_not_accepted:
    "This needs a normal sign-in. You are signed in to FER alone (identity service unreachable); it can wait until normal sign-in works again.",
  realm_token_required:
    "This needs a normal sign-in. With the FER local sign-in only incident cancellations can be reviewed.",
  mfa_required: "Sign in with your second factor (one-time code) first, then try again.",
  identity_unavailable: "The sign-in keys cannot be checked right now. Try again in a moment.",

  // Scope and two-person rules
  out_of_scope: "That record is outside your wards or stations, or you are not booked on the appliance it needs.",
  not_business_representative: "You are not the contact person of the business that owns these premises.",
  kyc_tier_too_low: "The owner's identity check is not complete enough to apply yet.",
  action_not_allowed:
    "You may not take this step: your role cannot, or it concerns premises you own or a bill you pay, or only the proposer may withdraw.",
  four_eyes_required:
    "A different person must do this step. Whoever proposed, wrote, submitted or captured it cannot also confirm, approve or review it, and nobody unlocks their own credential.",
  chief_required: "Only the chief may do this (an SOS with no number to call back, or an incident outside every ward).",
  conflict_of_interest: "You may not decide on something you are involved in.",

  // Intake
  outside_service_area: "The place is outside Lusaka's service area (outside every ward).",
  client_id_reused: "This form was already used for a different alert. Close it and start a new one.",
  invalid_capture_time: "The time given is in the future or too long ago. Check the date and time.",
  unknown_key: "One of the choices is no longer in the council's configuration. Refresh and choose again.",
  callback_required: "Phone the caller back and record the attempt before dismissing an SOS from the app.",
  alert_handled: "This alert has already been confirmed, linked or dismissed. Refresh to see it.",
  alert_not_handled: "This alert has not been handled yet, so there is nothing to reopen.",
  reopen_window_passed:
    "Only the person who handled it may reopen it, within 30 minutes. A dispatcher or the chief can reopen it at any time.",
  location_required: "The alert has no location yet. Drop a pin on the map or pick a place first.",
  phone_required: "There is no phone number for this caller. Type the number to send the link to.",
  link_expired: "The location link has expired. Send a new one.",
  too_many_fixes: "This location link has received the most positions allowed. Send a new link if needed.",
  photo_limit: "This alert already has the most photos allowed.",
  invalid_signature: "The message provider's signature did not match.",

  // Incidents and dispatch
  incident_not_open: "That incident is already cleared, closed or cancelled. Confirm a new incident instead.",
  incident_final: "This incident is already cleared, closed or cancelled, so it cannot change.",
  incident_not_cleared: "The incident is not cleared yet: every crew must be released or stood down first.",
  severity_not_higher: "Escalation must raise the severity. To lower it, change the incident instead (with a note).",
  not_on_scene: "No crew has reached the scene yet.",
  not_allowed_on_scene:
    "A crew has reached the scene, so the incident cannot be cancelled for this reason. It closes through its report.",
  status_not_forward: "That status is not ahead of the current one (a status never goes backwards). The time was kept if earlier.",
  dispatch_final: "This dispatch has already ended (released, declined or stood down).",
  dispatch_moved: "This dispatch moved to another incident meanwhile. Refresh and try again.",
  before_assignment: "That time is before the appliance was assigned. Check the time heard on the radio.",
  link_moved: "The location link moved to another alert meanwhile. Refresh and try again.",
  appliance_not_available: "The appliance is out of service or not available.",
  not_booked_on: "Nobody is booked on to that appliance.",
  already_booked_on: "You are already booked on to another appliance.",
  appliance_booked: "Another crew commander is booked on to that appliance.",
  dispatch_active: "An appliance is booked on or dispatched, so this cannot change now.",
  already_contained: "The incident is already contained.",
  concurrent_update: "Someone else changed this at the same moment. Refresh and try again.",
  report_not_submitted: "The report has not been submitted, so it cannot be approved or returned.",
  report_final: "The report has been submitted or approved, so it cannot be edited now.",
  stale_etag: "Someone else saved the report meanwhile. Refresh to see their changes, then edit again.",

  // Resources
  call_sign_taken: "Another appliance already uses that call sign.",
  station_code_taken: "Another station already uses that code.",
  already_assigned: "That person is already assigned to this station.",
  role_not_held: "That person does not hold the crew commander role. Ask for the role to be granted first.",
  assignment_ended: "That assignment has already ended.",

  // Prevention and money
  application_open: "The premises already has an open certificate application.",
  certificate_valid: "The premises' certificate is still valid; a renewal can start only near its end.",
  application_not_payable: "Nothing can be paid on this application now (it lapsed, was withdrawn or cancelled, or no bill is open).",
  application_not_awaiting_inspection: "This application is not waiting for an inspection.",
  reinspection_window_closed: "The time to ask for a re-inspection has passed. The owner applies again.",
  reinspection_open: "A re-inspection is already open for this application.",
  amount_out_of_bounds: "The fee is outside the council's allowed range for this fee code. Check the tariff configuration.",
  payment_in_progress: "A payment is in progress on this bill. Wait until it finishes, then try again.",
  already_paid: "This bill has already been paid.",
  bill_paid: "This bill has already been paid, so it cannot be cancelled.",
  bill_refused: "The council's payments service refused the bill.",
  checklist_incomplete: "Every checklist item must be answered.",
  critical_item_failed: "A critical checklist item failed, so the inspection cannot pass.",
  defects_required: "A failed inspection must list at least one defect.",
  outcome_not_allowed: "That outcome is not allowed for this kind of inspection.",
  certificate_not_valid: "Only a valid certificate can be revoked.",
  hydrant_code_taken: "Another hydrant already uses that code.",
  hydrant_not_synced: "The hydrant this survey belongs to has not been recorded yet.",
  hydrant_awaiting_review: "The hydrant this survey belongs to is waiting for its registration to be reviewed.",
  hydrant_rejected: "The hydrant this survey belongs to was refused or rejected on review, so the survey was not recorded.",
  rejected_on_review: "This field item was rejected on review.",
  not_in_ward: "Captured outside the officer's wards at the time, so another officer of the ward reviews it.",
  photo_not_uploaded: "A photo has not finished uploading yet.",
  risk_class_four_eyes: "This risk-class change affects a fee or certificate, so it needs a second person: use Propose a risk-class change.",
  owner_four_eyes: "The premises has a certificate or an open application, so an owner change needs a second person: use Propose an owner change.",
  occupancy_four_eyes: "Leaving an occupancy type that needs a certificate needs a second person: use Propose an occupancy change.",
  amount_mismatch: "The bill's amount did not match the fee.",

  // Four-eyes
  proposal_pending: "A proposal for this is already waiting for a decision. Review that one first.",
  proposal_mismatch: "The proposal changed meanwhile, so the confirmation did not match. Refresh and review it again.",
  proposal_not_pending: "This proposal has already been decided, withdrawn or has expired.",
  already_reviewed: "This held item has already been reviewed.",

  // Files
  wrong_purpose: "That file was uploaded for another use. Upload it again here.",
  unknown_file: "That file was not uploaded here, or its upload did not finish. Upload it again.",
  file_used: "That file is already attached to another record. Upload it again.",
  legal_hold: "This file is under legal hold and cannot be removed.",
  upload_not_complete: "The upload did not finish. Upload the file again.",
  upload_quota_exceeded: "The daily number of photo uploads has been reached.",
  purpose_not_allowed: "Your role may not upload this kind of file.",

  // Local sign-in
  identity_available: "Normal sign-in is working, so the FER local sign-in is not available. Sign in normally.",
  invalid_credentials: "The PIN or this console's device key is not right.",
  credential_locked: "This local credential is locked after too many wrong PINs. Ask a FER admin to unlock it.",
  role_snapshot_too_old: "Your FER roles were last seen too long ago. Sign in normally once to refresh them.",
  credential_limit: "You already have the most local credentials allowed (3). Remove one first.",
  pin_too_weak: "Choose a PIN that is not all one digit and not a straight run like 123456.",

  // Dependencies
  config_unavailable: "The council's FER configuration has never been loaded. Try again once it is available.",
  dependency_unavailable: "A council service FER depends on is temporarily unavailable. Nothing was lost; try again shortly.",
  files_unavailable: "The files service is temporarily unavailable. Try the photo again shortly.",
  gis_unavailable: "The map service is temporarily unavailable. FER's cached wards and places still work.",
  notify_unavailable: "The messaging service is temporarily unavailable. FER sends the SMS itself.",
  party_unavailable: "The council's people register is temporarily unavailable. Try again shortly.",
  payments_unavailable: "The council's payments service is temporarily unavailable. Nothing was lost; try again shortly.",
  refdata_unavailable: "The council's configuration service is temporarily unavailable. FER uses its last good copy.",
  refdata_error: "The council's configuration service answered with an error. FER uses its last good copy.",
  routing_unavailable: "Road travel times are unavailable; FER estimates by straight-line distance.",
  sms_unavailable: "The SMS gateway is temporarily unavailable. Try again shortly.",
  wards_unavailable: "The ward map has never been loaded. Try again once it is available.",
  service_unavailable: "A council service FER depends on is temporarily unavailable. Nothing was lost; try again shortly.",

  // The client's own
  list_truncated: "More records match than this screen can show. Narrow the filter (status, ward, dates) to see them all.",
  network_error: "The FER service cannot be reached. Check your connection and try again.",
  unexpected_response:
    "The FER service sent a reply this screen cannot read (for example a network sign-in page). Check your connection and try again.",
  unsafe_link: "The service returned a link this screen does not open (it is not a secure https link).",
};

/** An RFC 9457 problem returned by the FER API (or a network failure mapped to one). */
export class ApiProblem extends Error {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly detail?: string;
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = "ApiProblem";
    this.problem = problem;
    this.status = problem.status;
    this.code = problem.code;
    this.title = problem.title;
    this.detail = problem.detail;
  }

  /** Message to show to people: plain language, never a stack trace. */
  get userMessage(): string {
    return problemMessage(this.problem);
  }

  /** Field errors for validation_failed, as "field: message". */
  get fieldErrors(): string[] {
    const errors = Array.isArray(this.problem.errors) ? this.problem.errors : [];
    return errors.map((e) => `${(Array.isArray(e.loc) ? e.loc : []).filter((l) => l !== "body").join(".")}: ${e.msg}`);
  }

  /** An extra member of the problem, when the server sends one (e.g. `emergency_numbers`). */
  extra(name: string): unknown {
    return (this.problem as Record<string, unknown>)[name];
  }
}

export function problemMessage(p: Pick<Problem, "code" | "status" | "title" | "detail">): string {
  const known = Object.prototype.hasOwnProperty.call(PROBLEM_MESSAGES, p.code) ? PROBLEM_MESSAGES[p.code] : undefined;
  if (known) return known;
  // Codes this version does not know: a generic message per status (the reference code is shown beside it),
  // never the server's detail, which is written for developers.
  if (p.status === 410) return "This has expired or was deleted under the retention rules.";
  if (p.status === 409) return "This record changed or is not in a state that allows this. Refresh and try again.";
  if (p.status === 422 || p.status === 400) return "The service could not accept these details. Check the form and try again.";
  if (p.status === 403) return "You are not allowed to do this here.";
  if (p.status === 404) return PROBLEM_MESSAGES.not_found;
  if (p.status === 429) return PROBLEM_MESSAGES.rate_limited;
  if (p.status >= 500) return PROBLEM_MESSAGES.service_unavailable;
  return "Something went wrong. Please try again.";
}

/** Plain words for a reason code the backend puts in a per-item result (assignments, held items, batches). */
export function reasonMessage(code: string | null | undefined): string {
  if (!code) return "";
  return Object.prototype.hasOwnProperty.call(PROBLEM_MESSAGES, code) ? PROBLEM_MESSAGES[code] : "Refused by the service.";
}

function isProblem(x: unknown): x is Problem {
  return (
    typeof x === "object" &&
    x !== null &&
    typeof (x as Problem).code === "string" &&
    typeof (x as Problem).status === "number"
  );
}

function codeForStatus(status: number): string {
  switch (status) {
    case 401:
      return "unauthenticated";
    case 403:
      return "forbidden_role";
    case 404:
      return "not_found";
    case 429:
      return "rate_limited";
    case 503:
      return "service_unavailable";
    default:
      return `http_${status}`;
  }
}

/** Build an ApiProblem from a non-2xx response, tolerating bodies that are not problem+json. */
export async function problemFromResponse(res: Response): Promise<ApiProblem> {
  let body: unknown = undefined;
  try {
    const text = await res.text();
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = undefined;
  }
  if (isProblem(body)) {
    return new ApiProblem({ ...body, type: body.type ?? "about:blank", title: body.title ?? res.statusText });
  }
  return new ApiProblem({
    type: "about:blank",
    title: res.statusText || "Error",
    status: res.status,
    code: codeForStatus(res.status),
  });
}

export function networkProblem(cause: unknown): ApiProblem {
  return new ApiProblem({
    type: "urn:lcc:problem:network_error",
    title: "Network error",
    status: 0,
    code: "network_error",
    detail: cause instanceof Error ? cause.message : undefined,
  });
}

/** A 2xx reply that is not JSON (captive portal, proxy error page): a problem, never a stuck screen. */
export function unexpectedResponseProblem(status: number): ApiProblem {
  return new ApiProblem({
    type: "urn:lcc:problem:unexpected_response",
    title: "Unexpected response",
    status,
    code: "unexpected_response",
  });
}

/** A link from the service that is not https (http only for localhost in development builds). */
export function unsafeLinkProblem(): ApiProblem {
  return new ApiProblem({ type: "urn:lcc:problem:unsafe_link", title: "Unsafe link", status: 0, code: "unsafe_link" });
}
